import React, { useState } from "react";
import "../../styles/table.css";
import { formatDateTime } from "../../utils/formatDate";
import { formatHeader } from "../../functions/utils/util_functions";

interface Transaction {
  [key: string]: string | number | null;
}

interface ThresholdRule {
  min?: number;
  max?: number;
  minTime?: string;
  maxTime?: string;
}

interface SmallTableProps<T extends Transaction> {
  data: T[];
  showSearchControls?: boolean;
  rowClassName?: (row: T, index: number, data: T[]) => string;
  middleColumnKey?: string | string[];
  thresholds?: Record<string, ThresholdRule>;
}

const ITEMS_PER_PAGE = 10;

const SmallTableComponent = <T extends Transaction>({
  data,
  showSearchControls,
  rowClassName,
  middleColumnKey,
  thresholds,
}: SmallTableProps<T>): React.ReactElement => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchColumn, setSearchColumn] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  if (!data || data.length === 0) {
    return <div>No transactions found.</div>;
  }

  const headers = Object.keys(data[0]);

  const filteredData = data.filter((row) => {
    if (!searchColumn || !searchQuery) return true;
    const value = row[searchColumn];
    return (
      value !== null &&
      value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const timeToMinutes = (value: string | number | Date | null | undefined): number | null => {
    if (value === null || value === undefined) return null;

    if (value instanceof Date) {
      if (isNaN(value.getTime())) return null;
      return value.getHours() * 60 + value.getMinutes();
    }

    const str = String(value).trim();

    let m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(str);
    if (m) {
      const hh = parseInt(m[1], 10);
      const mm = parseInt(m[2], 10);
      if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
      return hh * 60 + mm;
    }

    m = /^(\d{1,2})\/(\d{1,2})\/(\d{4}),\s*(\d{1,2}):(\d{2}):(\d{2})$/.exec(str);
    if (m) {
      const hh = parseInt(m[4], 10);
      const mm = parseInt(m[5], 10);
      if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
      return hh * 60 + mm;
    }

    m = /^\d{4}-\d{2}-\d{2}T(\d{2}):(\d{2}):?(\d{2})?/.exec(str);
    if (m) {
      const hh = parseInt(m[1], 10);
      const mm = parseInt(m[2], 10);
      if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
      return hh * 60 + mm;
    }

    return null;
  };

  const getCellClass = (row: T, header: string): string => {
    const baseClass = Array.isArray(middleColumnKey)
      ? middleColumnKey.includes(header)
        ? "middle-column-border"
        : ""
      : middleColumnKey === header
      ? "middle-column-border"
      : "";

    const rule = thresholds?.[header];
    if (!rule) return baseClass;

    const value = row[header];
    if (value === null || value === undefined) return baseClass;

    const timeMinutes = timeToMinutes(value);
    if (timeMinutes !== null) {
      const minT = rule.minTime ? timeToMinutes(rule.minTime) : null;
      const maxT = rule.maxTime ? timeToMinutes(rule.maxTime) : null;

      if ((minT !== null && timeMinutes < minT) || (maxT !== null && timeMinutes > maxT)) {
        return `${baseClass} threshold-red`.trim();
      }
    }

    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      if ((rule.min !== undefined && numeric < rule.min) || (rule.max !== undefined && numeric > rule.max)) {
        return `${baseClass} threshold-red`.trim();
      }
    }

    return baseClass;
  };

  return (
    <div className="table-component">
      {showSearchControls !== false && (
        <div className="table-search-controls">
          <select
            value={searchColumn}
            onChange={(e) => setSearchColumn(e.target.value)}
          >
            <option value="">Select column</option>
            {headers.map((header) => (
              <option key={header} value={header}>
                {formatHeader(header)}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Search value..."
            value={searchQuery}
            onChange={handleSearch}
          />
        </div>
      )}

      <table className="small-trans-table">
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{formatHeader(header)}</th>
            ))}
          </tr>
        </thead>

        <tbody>
          {paginatedData.map((row, idx) => (
            <tr
              key={idx}
              className={rowClassName ? rowClassName(row, idx, data) : ""}
            >
              {headers.map((header) => {
                const cellValue = row[header];

                return (
                  <td key={header} className={getCellClass(row, header)}>
                    {(() => {
                      if (cellValue === null || cellValue === undefined) return "";

                      const strValue = cellValue.toString();

                      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(strValue)) {
                        return formatDateTime(strValue);
                      }

                      const numValue = Number(cellValue);
                      if (!Number.isNaN(numValue)) {
                        const rounded = Math.round(numValue * 10) / 10;
                        return rounded % 1 === 0 ? rounded : rounded.toFixed(1);
                      }

                      return strValue;
                    })()}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="table-pagination">
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default SmallTableComponent;
